import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Form, Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Check, ChevronDown, ChevronRight, ChevronUp, ArrowRight, ArrowLeft, Search } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { Utils } from "@/common/utils";
import Auth from "../../../common/auth/auth";
import CONFIG from "Config";
import { useFeatureGating, useFeatureGatingEnabled } from "@/hooks/feature-gating";
import { useSchoolsOptions } from "@/hooks/schools";
import { useQuery } from "@/hooks/router";
import { SimpleSchool } from "Stores/appStore";
import { appStore } from "Stores/index";

import Home from "@/images/icons/home.svg";
import HomeSolid from "@/images/icons/home-solid.svg";
import Dasboard from "@/images/icons/dashboard.svg";
import DasboardSolid from "@/images/icons/dashboard-solid.svg";
import LeftRightDash from "@/images/icons/left-right-dash.svg";
import LeftRightDashSolid from "@/images/icons/left-right-solid.svg";
import File from "@/images/icons/file.svg";
import FileSolid from "@/images/icons/file-solid.svg";
import UserGroup from "@/images/icons/user-group.svg";
import UserGroupSolid from "@/images/icons/user-group-solid.svg";
import Label from "@/images/icons/label.svg";
import LabelSolid from "@/images/icons/label-solid.svg";
import Student from "@/images/icons/student.svg";
import StudentSolid from "@/images/icons/student-solid.svg";
import Libaries from "@/images/icons/libraries.svg";
import LibariesSolid from "@/images/icons/libraries-solid.svg";
import BoxChecked from "@/images/icons/box-checked.svg";
import BoxCheckedSolid from "@/images/icons/box-check-solid.svg";
import StudentGroups from "@/images/icons/student-groups.svg";
import StudentGroupsSolid from "@/images/icons/student-group-solid.svg";
import AILearning from "@/images/icons/ai-learning.svg";
import AILearningSolid from "@/images/icons/ailearning-solid.svg";
import Calendar from "@/images/icons/calendar.svg";
import CalendarSolid from "@/images/icons/calendar-solid.svg";
import DashboardSpeed from "@/images/icons/dashboard-speed.svg";
import Instructure from "@/images/icons/instructure.svg";

import BookOpen from "@/images/icons/book-open.svg";
import Tabs from "@/images/icons/tabs.svg";
import TabsSolid from "@/images/icons/tabs-solid.svg";
import Settings from "@/images/icons/settings.svg";
import SettingsSolid from "@/images/icons/settings-solid.svg";

import TwoUsers from "@/images/icons/users.svg";
import TwoUsersSolid from "@/images/icons/users-solid.svg";
import UserProfile from "@/images/icons/user-circle.svg";
import UserProfileSolid from "@/images/icons/user-circle-solid.svg";
import UnfoldCaret from "@/images/icons/unfold-caret.svg";
import Logout from "@/images/icons/logout.svg";
import ArrowRotate from "@/images/icons/arrow-rotate.svg";
import UserList from "@/images/icons/user-list.svg";
import UserGear from "@/images/icons/user-gear.svg";

import { type DropDirection } from "react-bootstrap/esm/DropdownContext";

interface MenuItem {
    text: string;
    url: string;
    icon: { outline: JSX.Element; solid: JSX.Element };
    adminOnly?: boolean;
    isActiveCondition?: (isActive: boolean) => boolean;
}
interface SidebarProps {
    isCollapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
    auth: Auth;
}

type IconTooltipProps = {
    id: string;
    text: string;
    children: React.ReactNode;
};

const IconWithTooltip = ({ id, children, text }: IconTooltipProps) => (
    <OverlayTrigger
        key={id}
        trigger={["hover", "focus"]}
        overlay={
            <Tooltip id={id} className="custom-tooltip">
                {text}
            </Tooltip>
        }
        placement="right"
    >
        <span className="w-100">{children}</span>
    </OverlayTrigger>
);

export default function Sidebar({ isCollapsed, onCollapsedChange, auth }: SidebarProps) {
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isViewMoreExpanded, setIsViewMoreExpanded] = useState(true);
    const [username, setUsername] = useState("");

    const sidebarRef = useRef<HTMLDivElement | null>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const linksRef = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([]);

    const { enabled: categorizerEnabled } = useFeatureGatingEnabled("ui", "categorizer");
    const { enabled: courseCatalog } = useFeatureGatingEnabled("ui", "course_catalog");
    const { enabled: enfluxTermsEnabled } = useFeatureGatingEnabled("ingestible", "enflux_terms");
    const { enabled: canvasEnabled } = useFeatureGatingEnabled("ui", "canvas");
    const { enabled: studentGroupsEnabled } = useFeatureGatingEnabled("ui", "student_groups_permissions");

    const { data: schoolOptions } = useSchoolsOptions();

    const { mutate: mutateFeatureGating } = useFeatureGating();

    const query = useQuery();

    const dashboardGroupQuery = query.get("dashboardGroup");

    const hasSchoolOptions = schoolOptions && schoolOptions.length > 1;

    const generateUrl = (partialUrl = "") => {
        if (partialUrl === "") {
            return Utils.getLinkBase() + "/home";
        }
        return `${Utils.getLinkBase()}/${partialUrl}`;
    };

    const menuItems: MenuItem[] = [
        { text: "Home", url: generateUrl(""), icon: { outline: <Home />, solid: <HomeSolid /> } },
        { text: "All Dashboards", url: generateUrl("dashboards"), icon: { outline: <Dasboard />, solid: <DasboardSolid /> }, isActiveCondition: (isActive) => isActive && !dashboardGroupQuery },
        { text: "Action Plans", url: generateUrl("actionplans"), icon: { outline: <LeftRightDash />, solid: <LeftRightDashSolid /> } },
        { text: "Reports", url: generateUrl("reports"), icon: { outline: <File />, solid: <FileSolid /> }, adminOnly: true },
    ];

    const adminMenu = () => {
        const allMenuItems = [
            { text: "Users", url: generateUrl("users"), icon: { outline: <TwoUsers />, solid: <TwoUsersSolid /> }, condition: true },
            { text: "Labels", url: generateUrl("labels"), icon: { outline: <Label />, solid: <LabelSolid /> }, condition: true },
            { text: "Label Groups", url: generateUrl("labelgroups"), icon: { outline: <Tabs />, solid: <TabsSolid /> }, condition: true },
            { text: "Committees", url: generateUrl("committees"), icon: { outline: <UserGroup />, solid: <UserGroupSolid /> }, condition: true },
            { text: "Effectiveness Plans", url: generateUrl("institutionaleffectiveness"), icon: { outline: <BoxChecked />, solid: <BoxCheckedSolid /> }, condition: true },
            { text: "Students", url: generateUrl("students"), icon: { outline: <Student />, solid: <StudentSolid /> }, condition: Utils.isSuperAdmin() },
            { text: "Student Groups", url: generateUrl("studentgroups"), icon: { outline: <StudentGroups />, solid: <StudentGroupsSolid /> }, condition: Utils.hasAdminAccess() },
            { text: "Preferences", url: generateUrl("preferences"), icon: { outline: <Settings />, solid: <SettingsSolid /> }, condition: true },
            { text: "Competency Genie", url: generateUrl("competencygenie"), icon: { outline: <AILearning />, solid: <AILearningSolid /> }, condition: categorizerEnabled },
            { text: "Course Catalog", url: generateUrl("course-catalog"), icon: { outline: <Libaries />, solid: <LibariesSolid /> }, condition: courseCatalog },
            { text: "Enflux Terms", url: generateUrl("enflux-terms"), icon: { outline: <Calendar />, solid: <CalendarSolid /> }, condition: enfluxTermsEnabled },
            { text: "Canvas Configuration", url: generateUrl("canvas"), icon: { outline: <Instructure />, solid: <Instructure /> }, condition: canvasEnabled },
            { text: "Enflux Admin", url: generateUrl("internal"), icon: { outline: <Settings />, solid: <SettingsSolid /> }, condition: Utils.isSuperAdmin() },
        ];

        return allMenuItems.filter((item) => item.condition);
    };

    const adminMenuStartIndex = menuItems.length;

    const logout = () => {
        auth.handleLogout(() => {
            window.location.replace(`https://${CONFIG.auth.domain}/v2/logout?returnTo=${window.location.protocol}//${window.location.host}/static/logout.html&client_id=${CONFIG.auth.clientId}`);
        });
    };

    const toggleDropdown = () => {
        setIsViewMoreExpanded((prev) => !prev);
    };

    const toggleUserMenu = () => {
        setIsUserMenuOpen(!isUserMenuOpen);
    };

    const handleSwitchSchool = (nextSchool: SimpleSchool) => {
        setIsUserMenuOpen(false);
        // TODO use a different method to set nextSchool
        appStore.updateSelectedSchool(nextSchool, true);
        mutateFeatureGating();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLDivElement>, index: number) => {
        const totalLinks = linksRef.current.length;

        if (e.key === "Enter" || e.key === " ") {
            // e.preventDefault();
            if (!isUserMenuOpen) {
                toggleUserMenu();
            }
        }

        if (e.key === "Escape") {
            onCollapsedChange(false);
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const nextIndex = (index + 1) % totalLinks;
            linksRef.current[nextIndex]?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prevIndex = (index - 1 + totalLinks) % totalLinks;
            linksRef.current[prevIndex]?.focus();
        }
    };

    const closeMenuOnItem = () => {
        if (isCollapsed) return;

        onCollapsedChange(false);
    };

    function handleClickOutside(event) {
        const isToggleClick = event.target.closest("[data-toggle-sidebar]");
        if ((sidebarRef.current && !sidebarRef.current.contains(event.target)) || isToggleClick) {
            onCollapsedChange(false);
        }
    }

    useEffect(() => {
        const chosenSchoolInfo = Utils.getChosenSchoolInfo();
        if (chosenSchoolInfo) {
            setUsername(chosenSchoolInfo.name);
        }
    }, []);

    useEffect(() => {
        if (!isCollapsed) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isCollapsed]);

    return (
        <div ref={sidebarRef} className={`app-sidebar bg-light border-end transition-all fixed-top d-flex flex-column ${isCollapsed ? "collapsed" : "open shadow"}`}>
            <div className="p-3 border-bottom d-flex align-items-center" style={{ height: "56px" }}>
                <Link to={generateUrl()} className={`${isCollapsed ? "brand-image-icon" : "brand-image-logo"}`} />
            </div>

            <div className="toggle-sidebar-wrap" aria-label="sidebar collapse toggle container">
                <button className={`toggle-sidebar btn btn-ghost d-flex align-items-center justify-content-center p-1 ${isCollapsed ? "" : "shadow"}`} data-toggle-sidebar="true" aria-label="sidebar collapse button" onClick={() => onCollapsedChange(!isCollapsed)}>
                    {isCollapsed ? <ArrowRight /> : <ArrowLeft />}
                </button>
            </div>

            <div className="sidebar-scrollable-content d-flex flex-column justify-content-between" aria-label="menu items container">
                <Nav className="app-sidebar__nav flex-column pt-2">
                    <div className="d-flex flex-column p-2 w-100" ref={containerRef} tabIndex={0} style={{ outline: "none", gap: "6px" }}>
                        <div className={`title-block ${isCollapsed && "px-1"}`}>
                            <span>Menu</span>
                        </div>
                        {menuItems.map((item, index) => {
                            if (item.adminOnly && !Utils.hasAdminAccess()) return null;
                            return (
                                <IconWithTooltip key={item.url} id={item.url} text={item.text}>
                                    <NavLink

                                        to={item.url}
                                        className={({ isActive }) => {
                                            const customIsActive = item.isActiveCondition ? item.isActiveCondition(isActive) : isActive;
                                            return `nav-link px-2 py-2 d-flex align-items-center rounded-2 ${isCollapsed ? "justify-content-center" : ""} ${customIsActive ? "nav-link-active" : ""}`;
                                        }}
                                        ref={(el) => (linksRef.current[index] = el)}
                                        tabIndex={0}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onClick={closeMenuOnItem}
                                    >
                                        {({ isActive }) => {
                                            const customIsActive = item.isActiveCondition ? item.isActiveCondition(isActive) : isActive;
                                            return (
                                                <span
                                                    className="d-flex align-items-center gap-2"
                                                    style={{
                                                        color: customIsActive ? "#363636" : "#5C5C5C",
                                                        transition: "transform 0.2s ease, color 0.3s ease",
                                                    }}
                                                >
                                                    <span className="nav-icon d-flex justify-content-center">{isActive ? item.icon.solid : item.icon.outline}</span>

                                                    <span className="nav-text">{item.text}</span>
                                                </span>
                                            );
                                        }}
                                    </NavLink>
                                </IconWithTooltip>
                            );
                        })}
                    </div>

                    {/* View More Section */}
                    {Utils.hasAdminAccess() && (
                        <div className="app-sidebar__nav-admin d-flex flex-column p-2 position-relative border-top">
                            <div
                                onClick={toggleDropdown}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        toggleDropdown();
                                        setTimeout(() => document.getElementById("admin-menu-list")?.focus(), 100);
                                    }
                                }}
                                aria-expanded={isViewMoreExpanded}
                                aria-controls="admin-menu-list"
                                className={`title-block w-100 px-2 text-start d-flex align-items-center ${isCollapsed ? "justify-content-center" : "justify-content-between"}`}
                                style={{ cursor: "pointer" }}
                            >
                                <span className={`${isCollapsed && "d-none"}`}>{`View ${isViewMoreExpanded ? "less" : "more"}`}</span>
                                {isViewMoreExpanded ? <ChevronUp size={24} className="text-muted" /> : <ChevronDown size={24} className="text-muted" />}
                            </div>

                            <div id="admin-menu-list" role="menu" className={`admin-menu-list transition-all ${isViewMoreExpanded ? "" : "collapse"}`}>
                                <div className="w-100 overflow-hidden">
                                    {adminMenu().map((item, index) => {
                                        const adminMapIndex = adminMenuStartIndex + index;
                                        return (
                                            <IconWithTooltip key={item.url} id={item.url} text={item.text}>
                                                <NavLink to={item.url} className={(state) => `nav-link d-flex align-items-center rounded-2 admin-menu-item ${isCollapsed && "justify-content-center"} ${state.isActive ? "nav-link-active" : ""}`} tabIndex={0} ref={(el) => (linksRef.current[adminMapIndex] = el)} role="menuitem" onKeyDown={(e) => handleKeyDown(e, adminMapIndex)} onClick={closeMenuOnItem}>
                                                    {({ isActive }) => (
                                                        <span className="d-flex align-items-center gap-2">
                                                            {isActive ? item.icon.solid : item.icon.outline}
                                                            <span className="nav-text">{item.text}</span>
                                                        </span>
                                                    )}
                                                </NavLink>
                                            </IconWithTooltip>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </Nav>

                {/* Help Center section */}
                <div className="app-sidebar__support p-2 d-flex flex-column border-top" ref={containerRef}>
                    <div className={`title-block ${isCollapsed ? "px-1 text-center" : ""}`}>
                        <span>Help</span>
                    </div>

                    <IconWithTooltip id={"/system-status"} text={"System Status"}>
                        <Nav.Link onClick={closeMenuOnItem} href="https://status.enflux.com/?_gl=1*2jx2id*_ga*MTI1OTUyODY1Ni4xNzMyMTI2NDky*_ga_LBM9YRDN1C*MTc0MTg5Njg1Ni4zNzkuMS4xNzQxODk2ODYyLjAuMC4w" target="_blank" className={`px-2 py-2 d-flex align-items-center gap-2 rounded-2 ${isCollapsed && "justify-content-center"}`}>
                            <span className="d-flex align-items-center gap-2">
                                <DashboardSpeed />

                                <span className="nav-text">System Status</span>
                            </span>
                        </Nav.Link>
                    </IconWithTooltip>

                    <IconWithTooltip id="/knowledge-base" text={"Knowledge Base"}>
                        <Nav.Link onClick={closeMenuOnItem} href="https://knowledgebase.enflux.com/_hcms/mem/login?redirect_url=/en/" target="_blank" className={`px-2 py-2 d-flex align-items-center gap-2 rounded-2 ${isCollapsed && "justify-content-center"}`}>
                            <span className="d-flex align-items-center gap-2">
                                <BookOpen />
                                <span className="nav-text">Knowledge Base</span>
                            </span>
                        </Nav.Link>
                    </IconWithTooltip>
                </div>
            </div>

            {/* Footer User Section */}
            <div className="app-sidebar__user position-absolute bottom-0 start-0 bg-light border-top w-100 p-2" ref={userMenuRef}>
                <Dropdown autoClose="outside" drop={`${isCollapsed ? "end" : "up"}`} show={isUserMenuOpen} onToggle={setIsUserMenuOpen} className="w-100">
                    <Dropdown.Toggle className={`profile-card w-100 d-flex align-items-center justify-content-between rounded ${isUserMenuOpen ? "opened" : "bg-transparent"}`}>
                        <div className="profile-card__user d-flex align-items-center gap-2">
                            <div className="flex-flex-shrink-0 d-flex align-items-center justify-content-center">{isUserMenuOpen ? <UserProfileSolid /> : <UserProfile />}</div>
                            <span className={`profile-card__username ${isCollapsed ? "d-none" : ""}`}>{username}</span>
                        </div>
                        <UnfoldCaret />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <Dropdown.Item href={generateUrl("account/notifications")} className="btn btn-link btn-light text-decoration-none text-dark w-100 text-start d-flex align-items-center justify-content-between p-2">
                            <div className="d-flex align-items-center gap-2">
                                <UserGear />
                                <span className="font-base-size">Account Preference</span>
                            </div>
                        </Dropdown.Item>

                        {studentGroupsEnabled && (
                            <Dropdown.Item href={generateUrl("personal-student-groups")} className="btn btn-link btn-light text-decoration-none text-dark w-100 text-start d-flex align-items-center justify-content-between p-2">
                                <div className="d-flex align-items-center gap-2">
                                    <UserList />
                                    <span className="font-base-size">Personal Student Groups</span>
                                </div>
                            </Dropdown.Item>
                        )}

                        {hasSchoolOptions && (
                            <Dropdown.Item as="div" className="btn btn-link btn-light text-decoration-none text-dark w-100 text-start d-flex align-items-center justify-content-between p-2">
                                <SearchableDropdown value={Utils.getChosenSchool() ?? ""} items={schoolOptions} className="search-dropdown w-100" toggleElement={CustomToggle} onSelectItem={(school) => handleSwitchSchool(school)}></SearchableDropdown>
                            </Dropdown.Item>
                        )}

                        <Dropdown.Divider />

                        <Dropdown.Item as="button" onClick={logout} className="btn btn-link btn-light text-decoration-none text-dark w-100 text-start d-flex align-items-center justify-content-between p-2">
                            <div className="d-flex align-items-center gap-2">
                                <Logout />
                                <span className="font-base-size">Sign Out</span>
                            </div>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
}

type CustomToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const CustomToggle = React.forwardRef((props: CustomToggleProps, ref: React.Ref<HTMLDivElement>) => (
    <div
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            props.onClick?.(e);
        }}
        className="d-flex align-items-center justify-content-between gap-2"
    >
        <div className="d-flex align-items-center gap-2">
            <ArrowRotate />
            <span className="font-base-size">Switch Schools</span>
        </div>
        <ChevronRight size={20} className="text-muted" />
    </div>
));

CustomToggle.displayName = "CustomToggle";

interface SearchableDropdownProps {
    toggleElement: React.ElementType;
    items: SimpleSchool[];
    value: string;
    onSelectItem: (item: SimpleSchool) => void;
    className?: string;
    position?: DropDirection;
}

function SearchableDropdown({ value, onSelectItem, toggleElement, className, position = "end", items = [] }: SearchableDropdownProps) {
    const [searchTerm, setSearchTerm] = useState("");

    const filteredItems = items.filter((item) => item.display_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleSelect = (item) => {
        onSelectItem(item);
    };

    const isOptionEmpty = !filteredItems?.length;

    return (
        <Dropdown drop={position} align={"end"} className={className}>
            <Dropdown.Toggle id="dropdown-basic" className="w-100" as={toggleElement}></Dropdown.Toggle>

            <Dropdown.Menu>
                <>
                    <div className="searchbox d-flex align-items-center gap-2">
                        <Search />
                        <Form.Control value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} type="text" as={"input"} placeholder="Search" />
                    </div>
                </>

                <div className="dropdown-items-wrapper">
                    {filteredItems.map((school) => (
                        <Dropdown.Item key={school.school_id} as="div" role="option" className="item-option d-flex align-items-center justify-content-between" onClick={() => handleSelect(school)}>
                            <span>{school.display_name}</span>
                            {value === school.school_id && <Check />}
                        </Dropdown.Item>
                    ))}
                </div>

                {isOptionEmpty && (
                    <Dropdown.Item as="p" className="empty-method" disabled>
                        Schools not available.
                    </Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
}
